import { grey10, white } from '@zocdoc/branding/colors';
import { ContentPrimitives as APContentPrimitives } from '@zocdoc/components/src/AppointmentPicker';
import { breakpoint } from '@zocdoc/components/src/grid';
import { PAGE_X_AXIS_PADDING_BY_BREAKPOINT } from '@zocdoc/components/src/Layout';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import VisibilitySensor from 'react-visibility-sensor';

import {
    RESULT_CARD_X_MARGIN_XXSMALL,
    RESULT_CARD_X_MARGIN_MEDIUM,
    RESULT_CARD_X_MARGIN_LARGE,
    RESULT_CARD_Y_MARGIN_XXSMALL,
    RESULT_CARD_Y_MARGIN_LARGE,
} from '../../constants';

import { SearchResultsNonBookableLink } from './primitives';

/**
 * A layout-focused component that maintains the position and sizing of a search result item. The
 * intention for this component is you can freely stick a different thing into the first or last slot
 * as needed and maintain the same intrinsic sizing of the areas so things line up without tampering.
 */
class SearchResultRow extends React.PureComponent {
    static propTypes = {
        firstSlot: PropTypes.node,
        lastSlot: PropTypes.node,
        onScrolledIntoView: PropTypes.func, // for analytics
        searchResultId: PropTypes.string, // to be returned within onScrolledIntoView
    };

    constructor(props) {
        super(props);
        this.state = {
            trackScroll: !!props.onScrolledIntoView,
        };
    }

    handleWaypointEnter = visible => {
        const {
            onScrolledIntoView,
            searchResultId,
        } = this.props;
        if (visible && !!onScrolledIntoView) {
            this.setState({ trackScroll: false });
            onScrolledIntoView(searchResultId);
        }
    }

    render() {
        const {
            firstSlot,
            lastSlot,
            onScrolledIntoView,
            searchResultId,
            ...props
        } = this.props;

        const {
            trackScroll,
        } = this.state;

        return (
            <VisibilitySensor
                active={trackScroll}
                onChange={this.handleWaypointEnter}
            >
                <article {...props}>
                    <FirstSlot>
                        {firstSlot}
                    </FirstSlot>

                    <LastSlot>
                        {lastSlot}
                    </LastSlot>
                </article>
            </VisibilitySensor>
        );
    }
}

const FirstSlot = styled.div``;

export const AP_PERCENTAGE_XXL = 45;
export const AP_SIZE_XL = 425;
export const AP_SIZE_L = 425;
export const AP_SIZE_M = 300;

const LastSlot = styled.div`
    align-items: center;
    align-self: stretch;
    display: flex;
    flex-shrink: 0;
    margin-top: 21px;
    justify-content: center;
    overflow: hidden;
    padding: 0;
    position: relative;
    width: auto;

    > * {
        flex-grow: 1;
    }

    ${breakpoint.small} {
        margin-left: 96px;
        margin-top: 18px;
    }

    ${breakpoint.medium} {
        margin-left: auto;
        margin-top: 0;
        width: ${AP_SIZE_M}px;
    }

    ${breakpoint.large} {
        width: ${AP_SIZE_L}px;
    }

    ${breakpoint.xlarge} {
        width: ${AP_SIZE_XL}px;
    }

    ${breakpoint.xxlarge} {
        width: ${AP_PERCENTAGE_XXL}%;
    }
`;

/**
 * All the AP styling overrides here need to be at this level because a hover on the
 * whole result row is meant to trigger the idle/active styling for the timeslots.
 */
const Wrapper = styled(SearchResultRow)`
    background-color: ${white};
    display: flex;
    flex-direction: column;
    margin: ${RESULT_CARD_Y_MARGIN_XXSMALL}px ${RESULT_CARD_X_MARGIN_XXSMALL}px;
    padding: 12px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL - RESULT_CARD_X_MARGIN_XXSMALL}px;

    > :first-child {
        flex: 1 0;
        min-width: 0;
    }

    ${APContentPrimitives.MobileSlotWrapper} {
        width: 20vw;
    }

    ${APContentPrimitives.MobileSlot} {
        background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorMobile};
        border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorMobile};
        color: ${p => p.theme.components.AppointmentPicker.slot.textColorMobile};

        @media (pointer: fine) {
            &:hover {
                background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorHover};
                border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorHover};
                color: ${p => p.theme.components.AppointmentPicker.slot.textColorHover};
            }

            &:active {
                background-color: ${p => p.theme.components.AppointmentPicker.slot.bgColorActive};
                border-color: ${p => p.theme.components.AppointmentPicker.slot.borderColorActive};
                color: ${p => p.theme.components.AppointmentPicker.slot.textColorActive};
            }
        }
    }

    ${APContentPrimitives.MobileLoadingSlot} {
        background-image: repeating-linear-gradient(to right, ${grey10}, ${grey10} 20vw, white 20vw, white calc(20vw + 8px));
    }

    ${LastSlot} {
        margin-right: ${-1 * (PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL - RESULT_CARD_X_MARGIN_XXSMALL)}px;

        ${breakpoint.small} {
            margin-right: ${(-1 * (PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL - RESULT_CARD_X_MARGIN_XXSMALL)) + 8}px;
        }

        ${breakpoint.medium} {
            margin-right: 0;
        }

        ${APContentPrimitives.MobileNoTimesView} {
            ${breakpoint('xsmall', { down: true })} {
                margin-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL - RESULT_CARD_X_MARGIN_XXSMALL}px;
            }
        }

        ${APContentPrimitives.DesktopEmptySlot},
        ${APContentPrimitives.DesktopLoadingSlot} {
            background: ${p => p.theme.search.results.timesGridOverrides.idle.bgColorEmpty};
            border-color: ${p => p.theme.search.results.timesGridOverrides.idle.borderColorEmpty};
            color: ${p => p.theme.search.results.timesGridOverrides.idle.textColorEmpty};
        }

        ${APContentPrimitives.DesktopMoreButton},
        ${APContentPrimitives.DesktopNextAvailabilityLink},
        ${APContentPrimitives.DesktopPhoneNumberLink},
        ${APContentPrimitives.DesktopSlot},
        ${SearchResultsNonBookableLink} {
            background: ${p => p.theme.search.results.timesGridOverrides.idle.bgColor};
            border-color: ${p => p.theme.search.results.timesGridOverrides.idle.borderColor};
            color: ${p => p.theme.search.results.timesGridOverrides.idle.textColor};
        }
    }

    &:hover {
        ${APContentPrimitives.DesktopEmptySlot} {
            background: ${p => p.theme.search.results.timesGridOverrides.active.bgColorEmpty};
            border-color: ${p => p.theme.search.results.timesGridOverrides.active.borderColorEmpty};
            color: ${p => p.theme.search.results.timesGridOverrides.active.textColorEmpty};
        }

        ${APContentPrimitives.DesktopMoreButton},
        ${APContentPrimitives.DesktopNextAvailabilityLink},
        ${APContentPrimitives.DesktopPhoneNumberLink},
        ${APContentPrimitives.DesktopSlot},
        ${SearchResultsNonBookableLink} {
            background: ${p => p.theme.search.results.timesGridOverrides.active.bgColor};
            border-color: ${p => p.theme.search.results.timesGridOverrides.active.borderColor};
            color: ${p => p.theme.search.results.timesGridOverrides.active.textColor};

            &:hover {
                background: ${p => p.theme.search.results.timesGridOverrides.active.bgColorHover};
                border-color: ${p => p.theme.search.results.timesGridOverrides.active.borderColorHover};
                color: ${p => p.theme.search.results.timesGridOverrides.active.textColorHover};
            }

            &:active {
                background: ${p => p.theme.search.results.timesGridOverrides.active.bgColorActive};
                border-color: ${p => p.theme.search.results.timesGridOverrides.active.borderColorActive};
                color: ${p => p.theme.search.results.timesGridOverrides.active.textColorActive};
            }
        }
    }

    ${breakpoint.xsmall} {
        padding: 12px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XSMALL - RESULT_CARD_X_MARGIN_XXSMALL}px;
    }

    ${breakpoint.small} {
        padding: 12px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL - RESULT_CARD_X_MARGIN_XXSMALL}px;

        ${APContentPrimitives.MobileSlotWrapper} {
            width: 12.5vw;
        }
    }

    ${breakpoint('small', { down: true })} {
        > :first-child {
            flex-basis: auto;
        }
    }

    ${breakpoint.medium} {
        flex-direction: row;
        margin: ${RESULT_CARD_Y_MARGIN_XXSMALL}px ${RESULT_CARD_X_MARGIN_MEDIUM}px;
        padding: 16px 0 16px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM - RESULT_CARD_X_MARGIN_MEDIUM}px;
    }

    ${breakpoint.large} {
        margin: ${RESULT_CARD_Y_MARGIN_LARGE}px ${RESULT_CARD_X_MARGIN_LARGE}px;
        padding: 12px 0 12px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.LARGE - RESULT_CARD_X_MARGIN_LARGE}px;
    }

    ${breakpoint.xlarge} {
        padding: 12px 0 12px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XLARGE - RESULT_CARD_X_MARGIN_LARGE}px;
    }

    ${breakpoint.xxlarge} {
        padding: 12px 0 12px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXLARGE - RESULT_CARD_X_MARGIN_LARGE}px;
    }
`;

export default Wrapper;
